import type {
  ApiModelInlineCitation,
  Citation,
  FileAiAnnotation,
  InlineCitation,
  Message,
} from "@alaffia-technology-solutions/schema";

const deepLinkKeyMap = {
  FILE_PREVIEW: "file-preview",
} as const;

export interface FilePreviewDeepLinkData {
  annotations: FileAiAnnotation["coordinates"];
  fileId: FileAiAnnotation["file"]["id"];
  page: FileAiAnnotation["pageIndex"];
}

export type FilePreviewDeepLinkValue =
  | FilePreviewDeepLinkData
  | FilePreviewDeepLinkData[];

export interface DeepLinkData {
  "file-preview"?: Record<string, FilePreviewDeepLinkValue>;
  [citationType: string]: Record<string, unknown> | undefined;
}

export const createDeepLinkDataFromInlineCitations = (
  citations: InlineCitation[],
) => {
  const deepLinkData = citations.reduce<DeepLinkData>((acc, citation) => {
    const type = deepLinkKeyMap[citation.citationType];

    return {
      ...acc,
      [type]: {
        ...acc[type],
        [citation.key]: {
          annotations: citation.annotation.coordinates,
          fileId: citation.annotation.file.id,
          page: citation.annotation.pageIndex,
        },
      },
    };
  }, {});

  return deepLinkData;
};

export const createDeepLinkDataFromMessage = (message: Message) => {
  const citations = message.citations ?? [];

  return createDeepLinkDataFromInlineCitations(citations);
};

export const createDeepLinkDataFromApiInlineCitations = (
  citations: ApiModelInlineCitation[],
) => {
  const deepLinkData = citations.reduce<DeepLinkData>((acc, citation) => {
    const type = deepLinkKeyMap[citation.citation_type];
    if (!citation.annotations.length) {
      return acc;
    }

    const targets = citation.annotations.map((annotation) => ({
      annotations: annotation.coordinates,
      fileId: annotation.file.id,
      page: annotation.page_index,
    }));
    const existingTargets = acc[type]?.[citation.key];

    return {
      ...acc,
      [type]: {
        ...acc[type],
        [citation.key]: existingTargets
          ? [...getFilePreviewDeepLinkTargets(existingTargets), ...targets]
          : targets,
      },
    };
  }, {});

  return deepLinkData;
};

export const getFilePreviewDeepLinkTargets = (
  value: FilePreviewDeepLinkValue,
): FilePreviewDeepLinkData[] => (Array.isArray(value) ? value : [value]);

export const createDeepLinkDataFromMessageCitations = (
  citations: Citation[],
): DeepLinkData =>
  citations.reduce<DeepLinkData>((acc, citation) => {
    const existingTargets = acc["file-preview"]?.[citation.citation_key];

    const target: FilePreviewDeepLinkData = {
      fileId: citation.file_id,
      page: citation.page_index,
      annotations: [],
    };

    return {
      ...acc,
      "file-preview": {
        ...acc["file-preview"],
        [citation.citation_key]: existingTargets
          ? [...getFilePreviewDeepLinkTargets(existingTargets), target]
          : target,
      },
    };
  }, {});
